'use client';
import { PropsWithChildren } from 'react';
import {
  DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

let browserQueryClient: QueryClient | undefined = undefined;

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retryDelay: (attemptIndex: number) => {
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },
        staleTime: 30000,
        refetchInterval: 60000,
      },
    },
  });
}

export function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }

    return browserQueryClient;
  }
}

export function ReactQueryProvider({
  children,
  withDevtools = false,
  dehydratedState,
}: PropsWithChildren<{
  withDevtools?: boolean;
  dehydratedState?: DehydratedState;
}>) {
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>{children}</HydrationBoundary>
      {withDevtools && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}
